import { useEffect, useRef } from 'react';
import { IMap } from '../../services/exports/Interfaces';
import useAppMethods from '../utility/useAppMethods';
import paymentLog, { PaymentGatewayKey } from '../../services/helpers/PaymentLogger';
import {
  OTP_VERIFICATION_PASSWORD_HEADER,
  OTP_VERIFICATION_PHONE_NUMBER_HEADER,
} from '../../modals/OtpVerificationModal';
import type { CheckoutOptions } from './useOrderCheckout';
import { REMEMBER_DEVICE_HEADER } from '../../services/helpers/RememberedDevice';

export type OtpRetryFunction = (headers: IMap<string>, perCallOptions?: CheckoutOptions) => Promise<boolean>;

/**
 * The OTP step-up of a checkout: opens the global OTP modal and retries the order with the code.
 *
 * The returned promise resolves with the FINAL outcome of the OTP loop (success or cancel/failure).
 * Holding the outer await like this lets the Adyen wallet flow defer its actions.resolve/reject until
 * OTP fully settles — calling those in-flight would surface error UI in the dropin.
 *
 * The modal lives in GlobalModalsStack and outlives the screen that opened it, but the retry re-runs
 * that screen's confirmPayment. Once the customer has left the screen (Android back, swipe back) its
 * Stripe Elements are gone, and a code entered there placed an order the confirm could not pay
 * (SHOP-WEB-1Y). So a loop still waiting for its code is abandoned when the screen unmounts.
 */
export default function useOtpStepUp(gateway: PaymentGatewayKey) {
  const { toggleOtpVerificationModal } = useAppMethods();
  const abandonPendingLoopRef = useRef<(() => void) | null>(null);

  useEffect(() => () => abandonPendingLoopRef.current?.(), []);

  return (phoneNumber: string, retry: OtpRetryFunction): Promise<boolean> =>
    new Promise<boolean>((resolveOuter) => {
      const abandon = () => {
        abandonPendingLoopRef.current = null;
        paymentLog.info(gateway, 'otp_step_up_abandoned');
        toggleOtpVerificationModal();
        resolveOuter(false);
      };

      // A wrong code re-enters checkout, which opens a fresh loop in its place.
      const release = () => {
        if (abandonPendingLoopRef.current === abandon) {
          abandonPendingLoopRef.current = null;
        }
      };

      abandonPendingLoopRef.current = abandon;

      toggleOtpVerificationModal({
        openModal: true,
        phoneNumber,
        // `onSubmit` returns `true` as soon as the /orders POST clears the OTP gate (status != 400
        // OTP_REQUIRED), so the modal can close while the downstream confirm chain (wallet, 3DS,
        // redirect…) continues independently. If the OTP was wrong, the recursive checkout chain
        // re-opens a fresh modal AND this onSubmit resolves false, prompting the toast.
        onSubmit: (password: string, rememberDevice: boolean) =>
          new Promise<boolean>((resolveAcceptance) => {
            let settled = false;
            const settle = (accepted: boolean) => {
              if (settled) return;
              settled = true;
              resolveAcceptance(accepted);
            };

            retry(
              {
                [OTP_VERIFICATION_PHONE_NUMBER_HEADER]: phoneNumber,
                [OTP_VERIFICATION_PASSWORD_HEADER]: password,
                ...(rememberDevice ? { [REMEMBER_DEVICE_HEADER]: '1' } : {}),
              },
              {
                onOtpAccepted: () => {
                  release();
                  settle(true);
                },
              },
            ).then((finalResult) => {
              resolveOuter(finalResult);
              // Fallback if onOtpAccepted never fired (e.g. transport error before the response was
              // inspected). Treat as not-accepted so the modal exits its loading state; it stays open,
              // so the loop stays abandonable.
              settle(false);
            });
          }),
        onCancel: () => {
          release();
          resolveOuter(false);
        },
      });
    });
}
